import { Avatar, Box, Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import { type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";

import { Route } from "~services/router/Route";
import { FIELD_NAME_ADMIN_SELECT } from "~ui/molecules/Form/AdminSelect/AdminSelect";
import { MyOrganizationLinkIcons } from "~ui/molecules/MyOrganizationLinkIcons/MyOrganizationLinkIcons";
import { PhoneWithCopyIcon } from "~ui/molecules/PhoneWithCopyIcon/PhoneWithCopyIcon";
import {
  adminOrganizationKindChipColor,
  adminOrganizationKindFromOrganizationType,
  AdminOrganizationKindIcon,
  adminOrganizationKindLabel,
} from "~ui/organisms/AdminPortalHeader/adminOrganizationKindIcons";
import { type Organization } from "~ui/organisms/Organizations/types";
import { makeBubbleAvatarUrl } from "~utils/url";

const tOpenAdminPortal = "Open admin portal";

export interface MyOrganizationCardProps {
  organization: Organization;
  publicPagesEnabled?: boolean;
  /** When true, the card navigates to the admin portal for this organization. */
  isAdmin?: boolean;
}

export function MyOrganizationCard({
  organization,
  publicPagesEnabled = false,
  isAdmin = false,
}: MyOrganizationCardProps) {
  const navigate = useNavigate();
  const organizationKind = adminOrganizationKindFromOrganizationType(organization.organizationType);
  const avatarUrl =
    organization.avatarUrl ?? (organization.bubbleId ? makeBubbleAvatarUrl(organization.bubbleId) : undefined);
  const adminBubbleId = isAdmin ? organization.bubbleId : undefined;

  const handleCardClick = () => {
    if (!adminBubbleId) {
      return;
    }
    navigate(`${Route.admin()}?${FIELD_NAME_ADMIN_SELECT}=${adminBubbleId}`);
  };

  const handleCardKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCardClick();
    }
  };

  const adminInteractionProps = adminBubbleId
    ? {
        "aria-label": `${tOpenAdminPortal}: ${organization.name}`,
        onClick: handleCardClick,
        onKeyDown: handleCardKeyDown,
        role: "link" as const,
        tabIndex: 0,
      }
    : {};

  return (
    <Card
      {...adminInteractionProps}
      sx={{
        ...(adminBubbleId && {
          "&:hover": { borderColor: "primary.main" },
          cursor: "pointer",
        }),
        height: "100%",
      }}
      variant="outlined"
    >
      <CardContent>
        <Stack alignItems={{ sm: "flex-start", xs: "center" }} direction={{ sm: "row", xs: "column" }} gap={2}>
          <Avatar alt="" src={avatarUrl} sx={{ flexShrink: 0, height: { sm: 96, xs: 80 }, width: { sm: 96, xs: 80 } }}>
            <AdminOrganizationKindIcon kind={organizationKind} sx={{ fontSize: 40 }} />
          </Avatar>
          <Stack gap={0.75} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
              <AdminOrganizationKindIcon kind={organizationKind} sx={{ fontSize: 28 }} />
              <Chip
                color={adminOrganizationKindChipColor(organizationKind)}
                label={adminOrganizationKindLabel(organizationKind)}
                size="small"
                variant="outlined"
              />
              <Box sx={{ flex: 1 }} />
              <Box onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                <MyOrganizationLinkIcons
                  publicPagesEnabled={publicPagesEnabled}
                  slug={organization.slug}
                  website={organization.website}
                />
              </Box>
            </Stack>
            <Typography fontWeight={700} variant="h5">
              {organization.name}
            </Typography>
            {organization.address?.trim() ? (
              <Typography color="text.secondary" variant="body1">
                {organization.address}
              </Typography>
            ) : null}
            {organization.phoneNumber?.trim() ? (
              <Box
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                sx={{ mt: 0.5 }}
              >
                <PhoneWithCopyIcon phoneNumber={organization.phoneNumber.trim()} />
              </Box>
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
